<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import SharedAutocomplete from 'src/components/shared/SharedAutocomplete.vue';
import { formatCurrency } from 'src/utils/currency';
import { calcNetPrice, calcSalesPrice } from 'src/utils/article-utils';
import type { FetchOrganisationResponseLite } from 'src/api/models/fetchOrganisationResponseLite';
import type { CreateArticleDTO } from 'src/api/models/createArticleDTO';
import type { CreateArticleDTOMeasurement } from 'src/api/models/createArticleDTOMeasurement';
import { CreateArticleDTOMeasurement as Measurement } from 'src/api/models/createArticleDTOMeasurement';

interface Props {
  visible: boolean;
  disabled?: boolean;
  searchSupplierFn: (query: string, page: number) => Promise<{ data: any[]; page: number; total_pages: number }>;
}

const { visible, disabled = false, searchSupplierFn } = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'submit': [data: CreateArticleDTO];
}>();

const name = ref('');
const code = ref('');
const brandName = ref('');
const externalReference = ref('');
const measurement = ref<string>(Measurement.piece);
const location = ref('');
const selectedSupplier = ref<FetchOrganisationResponseLite | null>(null);
const purchaseGrossPrice = ref(0);
const discount = ref(0);
const minimumMargin = ref(0);

const calculatedNetPrice = computed(() => calcNetPrice(purchaseGrossPrice.value, discount.value));
const calculatedSalesPrice = computed(() => calcSalesPrice(calculatedNetPrice.value, minimumMargin.value));

const measurementOptions = [
  { label: 'Per stuk', value: Measurement.piece },
  { label: 'Per liter', value: Measurement.liter },
  { label: 'Per kg', value: Measurement.kilogram },
];

watch(() => visible, (val) => {
  if (val) {
    name.value = '';
    code.value = '';
    brandName.value = '';
    externalReference.value = '';
    measurement.value = Measurement.piece;
    location.value = '';
    selectedSupplier.value = null;
    purchaseGrossPrice.value = 0;
    discount.value = 0;
    minimumMargin.value = 0;
  }
});

const isValid = computed(() =>
  name.value.trim() && code.value.trim() && brandName.value.trim()
  && externalReference.value.trim() && measurement.value
  && selectedSupplier.value?.id,
);

function onSubmit () {
  if (!isValid.value) return;
  const dto: CreateArticleDTO = {
    name: name.value.trim(),
    code: code.value.trim(),
    brand_name: brandName.value.trim(),
    external_reference: externalReference.value.trim(),
    measurement: measurement.value as CreateArticleDTOMeasurement,
    location: location.value.trim(),
    description: null,
    sale_price: calculatedSalesPrice.value,
    purchase_gross_price: purchaseGrossPrice.value,
    purchase_net_price: calculatedNetPrice.value,
    minimum_margin: minimumMargin.value,
    supplier_id: selectedSupplier.value!.id!,
  };
  emit('submit', dto);
}

function onCancel () {
  emit('update:visible', false);
}
</script>

<template>
  <Dialog
    data-testid="article-create-dialog"
    :visible="visible"
    modal
    header="New Article"
    :style="{ width: '600px' }"
    @update:visible="emit('update:visible', $event)"
  >
    <div class="flex flex-col gap-4">
      <div>
        <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Identity</span>
        <div class="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Name *</label>
            <InputText
              data-testid="article-create-name"
              v-model="name"
              :disabled="disabled"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Code *</label>
            <InputText
              data-testid="article-create-code"
              v-model="code"
              :disabled="disabled"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Brand *</label>
            <InputText
              data-testid="article-create-brand"
              v-model="brandName"
              :disabled="disabled"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">External reference *</label>
            <InputText
              data-testid="article-create-external-ref"
              v-model="externalReference"
              :disabled="disabled"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Measurement *</label>
            <Select
              data-testid="article-create-measurement"
              v-model="measurement"
              :options="measurementOptions"
              option-label="label"
              option-value="value"
              :disabled="disabled"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Location</label>
            <InputText
              data-testid="article-create-location"
              v-model="location"
              :disabled="disabled"
              size="small"
              fluid
            />
          </div>
        </div>
      </div>

      <div>
        <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Supplier</span>
        <div class="mt-2">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Supplier *</label>
            <SharedAutocomplete
              data-testid="article-create-supplier"
              v-model="selectedSupplier"
              :fetch-fn="searchSupplierFn"
              option-label="name"
              placeholder="Search supplier..."
            />
          </div>
        </div>
      </div>

      <div>
        <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Purchase</span>
        <div class="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Gross price</label>
            <InputNumber
              data-testid="article-create-purchase-gross"
              v-model="purchaseGrossPrice"
              :disabled="disabled"
              mode="currency"
              currency="EUR"
              locale="nl-NL"
              :min="0"
              size="small"
              fluid
              input-class="font-mono"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Discount</label>
            <InputNumber
              data-testid="article-create-discount"
              v-model="discount"
              :disabled="disabled"
              suffix="%"
              :min="0"
              :max="100"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Net price</label>
            <div data-testid="article-create-purchase-net" class="font-mono text-sm px-2.5 py-1.5 border border-surface-300 dark:border-surface-600 rounded-md bg-surface-100 dark:bg-surface-800 text-surface-500 dark:text-surface-400">
              {{ formatCurrency(calculatedNetPrice) }}
            </div>
          </div>
        </div>
      </div>

      <div>
        <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Sales</span>
        <div class="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Minimum margin</label>
            <InputNumber
              data-testid="article-create-minimum-margin"
              v-model="minimumMargin"
              :disabled="disabled"
              suffix="%"
              :min="0"
              size="small"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-color-secondary">Sales price</label>
            <div data-testid="article-create-sales-price" class="font-mono text-sm px-2.5 py-1.5 border border-surface-300 dark:border-surface-600 rounded-md bg-surface-100 dark:bg-surface-800 text-surface-500 dark:text-surface-400">
              {{ formatCurrency(calculatedSalesPrice) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          data-testid="article-create-cancel"
          label="Cancel"
          severity="secondary"
          text
          @click="onCancel"
        />
        <Button
          data-testid="article-create-submit"
          label="Create article"
          icon="pi pi-check"
          severity="success"
          :disabled="!isValid || disabled"
          @click="onSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
